import { selectSortableMapSorted } from '@epilot/journey-logic-commons/src/next/store'
import type {
  Block,
  BlockId,
  Step,
  StepId
} from '@epilot/journey-logic-commons/src/types/next'

import type { DesiredState, WritableAppState } from '../types'

import {
  selectBlockById,
  selectIsAppReady,
  selectJourney,
  selectOriginalJourney,
  selectSelected,
  selectStepById
} from './config'

export const selectAppError = (state: WritableAppState) => state.appError

export const selectJourneyStepsSorted = (state: WritableAppState) => {
  const journey = selectJourney(state)

  return selectSortableMapSorted<StepId, Step>(journey?.steps)
}

export const selectInitialStepId = (state: WritableAppState) =>
  state.initialStepId

export const selectInitialStepIndex = (state: WritableAppState) => {
  if (typeof state.initialStepId === 'undefined') return undefined

  const journey = selectJourney(state)

  if (journey) return journey.steps?.[state.initialStepId]?.orderIndex

  const index = selectOriginalJourney(state)?.steps.findIndex(
    (step) => step.stepId === state.initialStepId
  )

  if (index === undefined || index === -1) return undefined

  return index
}

export const selectDesignBuilderState = (state: WritableAppState) =>
  state.designBuilderState

export const selectBlockValues = (state: WritableAppState) => state.blockValues

export const selectLastDesiredState = (
  state: WritableAppState
): DesiredState | undefined => {
  return state.logicsEngine.history.slice(-1)?.[0]
}

/**
 * Determines if the logics engine is locked.
 * Note: If the app is not ready, the engine is considered as locked.
 */
export const selectLogicsLock = (state: WritableAppState): boolean => {
  return !selectIsAppReady(state) || state.logicsEngine.locked
}

/**
 * Determines if a block is supposed to be shown or not.
 *
 * A block should be shown if:
 *
 * - Doesn't contain associated display conditions
 * - or if the display condition evaluted to true by the logics engine
 */
export const selectIsBlockShown = (
  state: WritableAppState,
  id: BlockId
): boolean => {
  const block = selectBlockById(state, id)
  const refId = block?.parent
    ? selectBlockById(state, block.parent)?.id
    : block?.id

  if (typeof refId === 'undefined') return false

  return (
    // doesn't have a display condition
    selectSelected(state)?.displayConditionBlocks?.has(refId) !== true ||
    // or is set to be shown by the logics engine
    selectLastDesiredState(state)?.show?.has(refId) === true
  )
}

/**
 * Return the blocks that are hidden in the current step because of display conditions.
 * If a stepId is provided, it will only return blocks that belong to that step.
 * If no stepId is provided, it will return all hidden blocks in the journey.
 * This is useful for determining which blocks are not visible to the user
 */
export const selectHiddenBlocks = (
  state: WritableAppState,
  stepId?: string
): Record<string, Block> => {
  const blocks = selectSelected(state)?.journey?.blocks ?? {}

  return Object.keys(blocks).reduce((acc, blockId) => {
    // if stepId is provided, only include blocks that belong to that step
    if (stepId && blocks[blockId].stepId !== stepId) {
      return acc
    }

    // check if the block is shown
    if (!selectIsBlockShown(state, blockId)) {
      acc[blockId] = blocks[blockId]
    }

    return acc
  }, {})
}

/**
 * Returns the blocks that should always be considered to be shown since
 * they're not associated with display conditions.
 */
export const selectInitiallyShownBlocks = (
  state: WritableAppState
): Set<BlockId> => {
  const selected = selectSelected(state)

  return new Set(
    Object.keys(selected?.journey?.blocks ?? {}).filter(
      (blockId) => selected?.displayConditionBlocks?.has(blockId) !== true
    )
  )
}

/**
 * Returns the current Step.
 * If not set, returns the first one by order index.
 */
export const selectCurrentStep = (state: WritableAppState) => {
  if (state.navigatedPath.length) {
    const currentStepId = state.navigatedPath[state.navigatedPath.length - 1]

    return selectStepById(state, currentStepId)
  }

  return undefined
}

export const selectCurrentStepOrThrow = (state: WritableAppState) => {
  const step = selectCurrentStep(state)

  if (!step) throw new Error('Expected current Step to be set')

  return step
}

/**
 * Infers whether the user can navigate back to a previous Step,
 * which should only be possible if the user has visited at least 2 different Steps.
 */
export const selectCanNavigateBack = (state: WritableAppState) => {
  return state.navigatedPath.length >= 2
}

/**
 * Returns the cache of blocks map for the currently selected Journey.
 */
export const selectBlocksMapCache = (state: WritableAppState) => {
  return selectSelected(state)?.blocksMap
}
